/**
 * ============================================================================
 * QUANTITY TIERS - Logique JavaScript Bidirectionnelle
 * ============================================================================
 *
 * Gère l'interaction bidirectionnelle entre le tableau de prix dégressifs
 * et le sélecteur de quantité du produit.
 *
 * Fonctionnalités:
 * 1. Clic sur bouton radio → Met à jour la quantité
 * 2. Modification quantité → Met à jour le bouton radio
 * 3. Feedback visuel (ligne active, animations)
 * 4. Indicateur textuel du palier actuel
 * 5. Calculs dynamiques des prix et économies
 *
 * @author Claude Code
 * @version 1.0.0
 * ============================================================================
 */

(function() {
  'use strict';

  /**
   * Configuration par défaut
   * Peut être surchargée via data-attributes ou paramètres
   */
  const CONFIG = {
    // Sélecteurs par défaut (peuvent être personnalisés)
    quantityInputSelector: '[data-quantity-input]',
    alternativeQuantitySelectors: [
      '[name="quantity"]',
      '.quantity__input',
      '#quantity-selector__input',
      'input[type="number"][name="quantity"]'
    ],

    // Classes CSS
    activeRowClass: 'quantity-tiers__row--active',
    highlightClass: 'quantity-tiers__row--highlight',

    // Durée de l'animation de highlight (ms)
    highlightDuration: 600,

    // Debug mode (afficher les logs dans la console)
    debug: false
  };

  /**
   * Classe principale QuantityTiers
   * Gère toute la logique d'interaction
   */
  class QuantityTiers {
    /**
     * Constructeur
     * @param {HTMLElement} container - L'élément conteneur .quantity-tiers
     */
    constructor(container) {
      this.container = container;
      this.sectionId = container.dataset.sectionId;
      this.tiers = [];
      this.currentTier = null;
      this.quantityInput = null;

      // Éléments du DOM
      this.radioButtons = [];
      this.tierRows = [];
      this.currentTierIndicator = null;
      this.currentTierLabel = null;
      this.currentTierDiscount = null;

      // Initialisation
      this.init();
    }

    /**
     * Initialisation principale
     */
    init() {
      try {
        this.log('Initializing QuantityTiers...');

        // Charger la configuration des paliers depuis le JSON
        this.loadTiersConfig();

        // Récupérer tous les éléments du DOM
        this.cacheDOMElements();

        // Trouver le sélecteur de quantité
        this.findQuantityInput();

        // Attacher les événements
        this.attachEventListeners();

        // Initialiser l'état par défaut
        this.initializeDefaultState();

        this.log('QuantityTiers initialized successfully', this);
      } catch (error) {
        console.error('[QuantityTiers] Initialization error:', error);
      }
    }

    /**
     * Charger la configuration des paliers depuis le script JSON
     */
    loadTiersConfig() {
      const configScript = this.container.querySelector('[data-tiers-config]');

      if (!configScript) {
        throw new Error('Tiers configuration script not found');
      }

      try {
        const config = JSON.parse(configScript.textContent);
        this.tiers = config.tiers;
        this.basePrice = config.basePrice;
        this.sectionId = config.sectionId;

        this.log('Tiers configuration loaded:', config);
      } catch (error) {
        throw new Error('Failed to parse tiers configuration: ' + error.message);
      }
    }

    /**
     * Mettre en cache tous les éléments du DOM nécessaires
     */
    cacheDOMElements() {
      // Boutons radio
      this.radioButtons = Array.from(
        this.container.querySelectorAll('[data-tier-radio]')
      );

      // Lignes du tableau
      this.tierRows = Array.from(
        this.container.querySelectorAll('[data-tier-row]')
      );

      // Indicateur de palier actuel
      this.currentTierIndicator = this.container.querySelector('[data-current-tier-indicator]');
      this.currentTierLabel = this.container.querySelector('[data-current-tier-label]');
      this.currentTierDiscount = this.container.querySelector('[data-current-tier-discount]');

      this.log('DOM elements cached:', {
        radioButtons: this.radioButtons.length,
        tierRows: this.tierRows.length
      });
    }

    /**
     * Trouver le sélecteur de quantité dans la page
     * Scoped to the closest product container to avoid cross-instance conflicts
     * (e.g. quickshop modal open while a product page is also in the DOM)
     */
    findQuantityInput() {
      // Walk up to find the closest scoping ancestor: product--outer or modal
      const scope = this.container.closest('.product--outer, .modal--quickshop-full, .modal--quickshop-slim')
        || document;

      // Try primary selector within scope
      this.quantityInput = scope.querySelector(CONFIG.quantityInputSelector);

      // Try alternative selectors within scope
      if (!this.quantityInput) {
        for (const selector of CONFIG.alternativeQuantitySelectors) {
          this.quantityInput = scope.querySelector(selector);
          if (this.quantityInput) {
            this.log(`Quantity input found with selector: ${selector}`);
            break;
          }
        }
      }

      // Try section-specific selector within scope
      if (!this.quantityInput && this.sectionId) {
        const sectionSpecificSelector = `#quantity-selector__input-${this.sectionId}`;
        this.quantityInput = scope.querySelector(sectionSpecificSelector);

        if (this.quantityInput) {
          this.log(`Quantity input found with section ID: ${sectionSpecificSelector}`);
        }
      }

      if (!this.quantityInput) {
        console.warn('[QuantityTiers] Quantity input not found. Tried selectors:',
          [CONFIG.quantityInputSelector, ...CONFIG.alternativeQuantitySelectors]
        );
      } else {
        this.log('Quantity input found:', this.quantityInput);
      }
    }

    /**
     * Attacher tous les événements
     */
    attachEventListeners() {
      // Événement sur les boutons radio
      this.radioButtons.forEach((radio) => {
        radio.addEventListener('change', (e) => this.handleRadioChange(e));
      });

      // Événement sur les lignes du tableau (clic pour sélectionner)
      this.tierRows.forEach((row) => {
        row.addEventListener('click', (e) => this.handleRowClick(e));
      });

      // Événement sur le sélecteur de quantité
      if (this.quantityInput) {
        this.quantityInput.addEventListener('input', (e) => this.handleQuantityChange(e));
        this.quantityInput.addEventListener('change', (e) => this.handleQuantityChange(e));

        // Observer pour détecter les changements via les boutons +/-
        this.observeQuantityChanges();
      }

      this.log('Event listeners attached');
    }

    /**
     * Observer les changements de valeur de l'input (pour les boutons +/-)
     */
    observeQuantityChanges() {
      // Sauvegarder la valeur précédente
      this.previousQuantity = parseInt(this.quantityInput.value, 10) || 1;

      // Observer les changements d'attribut "value"
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === 'attributes' && mutation.attributeName === 'value') {
            const newValue = parseInt(this.quantityInput.value, 10);
            if (newValue !== this.previousQuantity && !isNaN(newValue)) {
              this.log(`Quantity changed via buttons: ${this.previousQuantity} → ${newValue}`);
              this.previousQuantity = newValue;
              this.updateTierFromQuantity(newValue);
            }
          }
        });
      });

      observer.observe(this.quantityInput, {
        attributes: true,
        attributeFilter: ['value']
      });

      // Polling de secours pour les navigateurs/scripts qui modifient .value directement
      setInterval(() => {
        const currentValue = parseInt(this.quantityInput.value, 10) || 1;
        if (currentValue !== this.previousQuantity) {
          this.log(`Quantity changed (polling): ${this.previousQuantity} → ${currentValue}`);
          this.previousQuantity = currentValue;
          this.updateTierFromQuantity(currentValue);
        }
      }, 300); // Vérifier toutes les 300ms
    }

    /**
     * Initialiser l'état par défaut (palier 1 avec quantité actuelle)
     */
    initializeDefaultState() {
      if (this.quantityInput) {
        const currentQuantity = parseInt(this.quantityInput.value, 10) || 1;
        this.updateTierFromQuantity(currentQuantity);
      } else {
        // Si pas de sélecteur trouvé, activer le palier 1 par défaut
        this.setActiveTier(1);
      }
    }

    /**
     * FLUX 1: Gestion du changement de bouton radio
     * Bouton radio cliqué → Met à jour la quantité
     */
    handleRadioChange(event) {
      const radio = event.target;
      const tierIndex = parseInt(radio.dataset.tierIndex, 10);
      const quantity = parseInt(radio.value, 10);

      this.log(`Radio changed: tier ${tierIndex}, quantity ${quantity}`);

      // Mettre à jour la quantité dans le sélecteur
      if (this.quantityInput) {
        this.quantityInput.value = quantity;

        // Déclencher un événement 'change' pour que les autres scripts réagissent
        const changeEvent = new Event('change', { bubbles: true });
        this.quantityInput.dispatchEvent(changeEvent);
      }

      // Mettre à jour l'affichage
      this.setActiveTier(tierIndex);
    }

    /**
     * Gestion du clic sur une ligne du tableau
     * Permet de cliquer n'importe où sur la ligne pour sélectionner
     */
    handleRowClick(event) {
      // Éviter de déclencher si on clique directement sur le radio
      if (event.target.type === 'radio') {
        return;
      }

      const row = event.currentTarget;
      const tierIndex = parseInt(row.dataset.tierRow, 10);
      const radio = this.radioButtons.find(r => parseInt(r.dataset.tierIndex, 10) === tierIndex);

      if (radio) {
        radio.checked = true;

        // Déclencher l'événement change manuellement
        const changeEvent = new Event('change', { bubbles: true });
        radio.dispatchEvent(changeEvent);
      }
    }

    /**
     * FLUX 2: Gestion du changement de quantité
     * Quantité modifiée → Met à jour le bouton radio
     */
    handleQuantityChange(event) {
      const quantity = parseInt(event.target.value, 10);

      if (isNaN(quantity) || quantity < 1) {
        return;
      }

      this.log(`Quantity changed to: ${quantity}`);

      // Déterminer le palier correspondant
      this.updateTierFromQuantity(quantity);
    }

    /**
     * Déterminer et activer le palier correspondant à une quantité
     * @param {number} quantity - La quantité actuelle
     */
    updateTierFromQuantity(quantity) {
      // Trouver le palier correspondant
      const matchingTier = this.tiers.find(tier =>
        quantity >= tier.min && quantity <= tier.max
      );

      if (matchingTier) {
        this.log(`Quantity ${quantity} matches tier ${matchingTier.index}`);

        // Cocher le bon bouton radio
        const radio = this.radioButtons.find(r =>
          parseInt(r.dataset.tierIndex, 10) === matchingTier.index
        );

        if (radio && !radio.checked) {
          radio.checked = true;
        }

        // Mettre à jour l'affichage
        this.setActiveTier(matchingTier.index);
      }
    }

    /**
     * Définir le palier actif et mettre à jour l'affichage
     * @param {number} tierIndex - L'index du palier (1, 2, 3)
     */
    setActiveTier(tierIndex) {
      const tier = this.tiers.find(t => t.index === tierIndex);

      if (!tier) {
        console.error(`[QuantityTiers] Tier ${tierIndex} not found`);
        return;
      }

      this.currentTier = tier;

      // Mettre à jour les classes CSS des lignes
      this.tierRows.forEach((row) => {
        const rowTierIndex = parseInt(row.dataset.tierRow, 10);

        if (rowTierIndex === tierIndex) {
          // Activer cette ligne
          row.classList.add(CONFIG.activeRowClass);

          // Ajouter animation de highlight
          row.classList.add(CONFIG.highlightClass);
          setTimeout(() => {
            row.classList.remove(CONFIG.highlightClass);
          }, CONFIG.highlightDuration);
        } else {
          // Désactiver les autres lignes
          row.classList.remove(CONFIG.activeRowClass);
        }
      });

      // Mettre à jour l'indicateur textuel
      this.updateCurrentTierIndicator(tier);

      this.log(`Active tier set to: ${tierIndex}`, tier);
    }

    /**
     * Mettre à jour l'indicateur textuel du palier actuel
     * @param {Object} tier - L'objet palier
     */
    updateCurrentTierIndicator(tier) {
      if (!this.currentTierIndicator) {
        return;
      }

      // Mettre à jour le label (ex: "5+ unités")
      if (this.currentTierLabel) {
        const unitText = tier.min === 1 ? 'unité' : 'unités';
        this.currentTierLabel.textContent = `${tier.label} ${unitText}`;
      }

      // Mettre à jour la description de la réduction
      if (this.currentTierDiscount) {
        this.currentTierDiscount.textContent = tier.discountLabel;
      }
    }

    /**
     * Logger pour le mode debug
     */
    log(...args) {
      if (CONFIG.debug) {
        console.log('[QuantityTiers]', ...args);
      }
    }
  }

  /**
   * Initialisation automatique au chargement du DOM
   */
  function initializeQuantityTiers() {
    const containers = document.querySelectorAll('[data-quantity-tiers]');

    if (containers.length === 0) {
      return;
    }

    console.log(`[QuantityTiers] Found ${containers.length} instance(s)`);

    containers.forEach((container) => {
      try {
        new QuantityTiers(container);
      } catch (error) {
        console.error('[QuantityTiers] Failed to initialize instance:', error);
      }
    });
  }

  /**
   * Point d'entrée principal
   * Initialiser quand le DOM est prêt
   */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeQuantityTiers);
  } else {
    // Le DOM est déjà chargé
    initializeQuantityTiers();
  }

  /**
   * Support pour les thèmes qui chargent dynamiquement le contenu
   * (quickshop, AJAX, etc.)
   */
  window.QuantityTiers = QuantityTiers;
  window.initializeQuantityTiers = initializeQuantityTiers;

  /**
   * Mutation Observer pour détecter l'ajout dynamique de tableaux
   * Utile pour les quickshops ou modals qui se chargent après
   */
  const observeNewTiers = () => {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1) { // ELEMENT_NODE
            // Vérifier si le nœud lui-même est un quantity-tiers
            if (node.hasAttribute && node.hasAttribute('data-quantity-tiers')) {
              new QuantityTiers(node);
            }

            // Vérifier les enfants
            const nestedContainers = node.querySelectorAll && node.querySelectorAll('[data-quantity-tiers]');
            if (nestedContainers && nestedContainers.length > 0) {
              nestedContainers.forEach((container) => {
                new QuantityTiers(container);
              });
            }
          }
        });
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  };

  // Activer l'observer après le chargement initial
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', observeNewTiers);
  } else {
    observeNewTiers();
  }

  /**
   * API publique pour activer/désactiver le mode debug
   */
  window.QuantityTiersDebug = {
    enable: () => {
      CONFIG.debug = true;
      console.log('[QuantityTiers] Debug mode enabled');
    },
    disable: () => {
      CONFIG.debug = false;
      console.log('[QuantityTiers] Debug mode disabled');
    }
  };

})();
